import React, { useState } from "react";
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  TextInput,
  Switch,
  Button,
  Box,
} from "@mantine/core";
import { IconChevronLeft, IconExternalLink } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import {
  hasFieldError,
  getFieldErrorMessage,
  getInputStyles,
  getLabelStyles,
  getLabelText,
} from "../utils/validation";

interface FooterContent {
  showSocialIcons?: boolean;
  instagram?: string;
  facebook?: string;
  twitter?: string;
  tiktok?: string;
}

interface WebsiteGeneratorFooterContentProps {
  footerContent: FooterContent;
  onContentChange: (content: FooterContent) => void;
  onBack: () => void;
  missingFields?: string[];
  // Validation prop for field-level highlighting
  requiredValidation?: { [field: string]: boolean };
}

const WebsiteGeneratorFooterContent: React.FC<
  WebsiteGeneratorFooterContentProps
> = ({
  footerContent,
  onContentChange,
  onBack,
  missingFields = [],
  requiredValidation = {},
}) => {
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Handle input changes
  const handleInputChange = (field: keyof FooterContent, value: string) => {
    // Validate character limit
    if (value.length > 100) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: `${field} cannot exceed 100 characters`,
      }));
      return;
    }

    // Clear validation error
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });

    // Update content immediately
    onContentChange({
      ...(footerContent || {}),
      [field]: value,
    });
  };

  // Handle switch changes
  const handleSwitchChange = (field: keyof FooterContent, checked: boolean) => {
    onContentChange({
      ...(footerContent || {}),
      [field]: checked,
    });
  };

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between" align="center">
        <ActionIcon variant="subtle" onClick={onBack}>
          <IconChevronLeft size={16} />
        </ActionIcon>
        <Text fw={600} size="lg">
          Footer
        </Text>
      </Group>

      {/* Show Social Media Icons */}
      <Box>
        <Group justify="space-between" align="flex-start">
          <Box style={{ flex: 1 }}>
            <Text size="sm" fw={500} mb="xs">
              Show Social Media Icons
            </Text>
            <Text size="xs" c="dimmed">
              Share your socials with your clients
            </Text>
          </Box>
          <Switch
            checked={footerContent.showSocialIcons || false}
            onChange={(event) =>
              handleSwitchChange("showSocialIcons", event.currentTarget.checked)
            }
            style={{ marginTop: "4px" }}
          />
        </Group>
      </Box>

      {/* Social Media Fields */}
      {footerContent.showSocialIcons && (
        <Stack gap="sm">
          <Box>
            <Text
              size="sm"
              fw={500}
              mb="xs"
              style={getLabelStyles(
                "instagram",
                requiredValidation,
                footerContent.instagram,
              )}
            >
              {getLabelText(
                "Instagram",
                "instagram",
                requiredValidation,
                footerContent.instagram,
              )}
            </Text>
            <TextInput
              placeholder=""
              value={footerContent.instagram || ""}
              onChange={(event) =>
                handleInputChange("instagram", event.currentTarget.value)
              }
              error={getFieldErrorMessage(
                "instagram",
                requiredValidation,
                validationErrors,
                footerContent.instagram,
              )}
              maxLength={100}
              styles={getInputStyles(
                "instagram",
                requiredValidation,
                missingFields,
                "socials.instagram_url",
                footerContent.instagram,
              )}
            />
          </Box>

          <Box>
            <Text
              size="sm"
              fw={500}
              mb="xs"
              style={getLabelStyles(
                "facebook",
                requiredValidation,
                footerContent.facebook,
              )}
            >
              {getLabelText(
                "Facebook",
                "facebook",
                requiredValidation,
                footerContent.facebook,
              )}
            </Text>
            <TextInput
              placeholder=""
              value={footerContent.facebook || ""}
              onChange={(event) =>
                handleInputChange("facebook", event.currentTarget.value)
              }
              error={getFieldErrorMessage(
                "facebook",
                requiredValidation,
                validationErrors,
                footerContent.facebook,
              )}
              maxLength={100}
              styles={getInputStyles(
                "facebook",
                requiredValidation,
                missingFields,
                "socials.facebook_url",
                footerContent.facebook,
              )}
            />
          </Box>

          <Box>
            <Text
              size="sm"
              fw={500}
              mb="xs"
              style={getLabelStyles(
                "twitter",
                requiredValidation,
                footerContent.twitter,
              )}
            >
              {getLabelText(
                "Twitter",
                "twitter",
                requiredValidation,
                footerContent.twitter,
              )}
            </Text>
            <TextInput
              placeholder=""
              value={footerContent.twitter || ""}
              onChange={(event) =>
                handleInputChange("twitter", event.currentTarget.value)
              }
              error={getFieldErrorMessage(
                "twitter",
                requiredValidation,
                validationErrors,
                footerContent.twitter,
              )}
              maxLength={100}
              styles={getInputStyles(
                "twitter",
                requiredValidation,
                missingFields,
                "socials.twitter_url",
                footerContent.twitter,
              )}
            />
          </Box>

          <Box>
            <Text
              size="sm"
              fw={500}
              mb="xs"
              style={getLabelStyles(
                "tiktok",
                requiredValidation,
                footerContent.tiktok,
              )}
            >
              {getLabelText(
                "Tiktok",
                "tiktok",
                requiredValidation,
                footerContent.tiktok,
              )}
            </Text>
            <TextInput
              placeholder=""
              value={footerContent.tiktok || ""}
              onChange={(event) =>
                handleInputChange("tiktok", event.currentTarget.value)
              }
              error={getFieldErrorMessage(
                "tiktok",
                requiredValidation,
                validationErrors,
                footerContent.tiktok,
              )}
              maxLength={100}
              styles={getInputStyles(
                "tiktok",
                requiredValidation,
                missingFields,
                "socials.tiktok_url",
                footerContent.tiktok,
              )}
            />
          </Box>
        </Stack>
      )}

      {/* Settings Link */}
      <Group gap="xs" mt="md">
        <Text size="sm" c="dimmed">
          Visit
        </Text>
        <Button
          variant="subtle"
          color="blue"
          size="sm"
          rightSection={<IconExternalLink size={14} />}
          onClick={() => navigate("/admin/settings/studio")}
        >
          Settings &gt; Studio
        </Button>
        <Text size="sm" c="dimmed">
          to edit address and business hours.
        </Text>
      </Group>
    </Stack>
  );
};

export default WebsiteGeneratorFooterContent;
